import { apiClient } from './apiClient';
import { simpleApiClient } from './simpleApiClient';
import { 
  Event, 
  EventFormData, 
  EventFilters, 
  EventListResponse, 
  EventCategory, 
  EventSeries,
  EventPermissions,
  StatusTransitionOptions,
  EventActionData,
  InvitationLinkData,
  BusinessRuleValidation,
  EventHistoryEntry
} from '../types/event';

// Helper function to enrich event data with computed fields
const enrichEventData = (event: any): Event => {
  // Add rejection reason from access_control_flags
  if (event.access_control_flags?.rejection_reason) {
    event.rejection_reason = event.access_control_flags.rejection_reason;
  }
  
  return event as Event;
};

// API Response wrapper
interface APIResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error?: {
    type: string;
    message: string;
    details: any;
  };
}

// Event API service
export const eventAPI = {
  // Get all events with filters and pagination
  getEvents: async (
    filters: EventFilters = {},
    page: number = 1,
    pageSize: number = 9,
    forceRefresh: boolean = false
  ): Promise<EventListResponse> => {
    const params = new URLSearchParams();
    
    // Add pagination
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    
    // Add filters
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach(status => params.append('status', status));
    }
    if (filters.category_id) {
      params.append('category', filters.category_id.toString());
    }
    if (filters.series_id) {
      params.append('series_id', filters.series_id);
    }
    if (filters.host_id) {
      params.append('host_id', filters.host_id);
    }
    if (filters.date_from) {
      params.append('date_from', filters.date_from);
    }
    if (filters.date_to) {
      params.append('date_to', filters.date_to);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.location) {
      params.append('location', filters.location);
    }
    
    // Add series_main_only parameter to show only main events from series (default: true)
    // This prevents showing duplicate events when series have multiple events
    const seriesMainOnly = filters.series_main_only !== undefined ? filters.series_main_only : true;
    params.append('series_main_only', seriesMainOnly.toString());
    
    // Add ordering parameter
    if (filters.ordering) {
      params.append('ordering', filters.ordering);
    }

    // Use enhanced API client with caching for better performance (cache for 2 minutes)
    // Skip cache if force refresh is requested
    const cacheOptions = forceRefresh ? { cache: false } : { cache: true, ttl: 2 * 60 * 1000 };
    const response = await apiClient.get<APIResponse<EventListResponse>>(
      `/events/?${params.toString()}`,
      cacheOptions
    );
    
    // Handle different response structures and enrich event data
    let finalResponse: any;
    
    if (response.data?.data) {
      // Standard APIResponse<EventListResponse> structure
      finalResponse = response.data.data;
    } else if (response.data) {
      // Direct data response
      finalResponse = response.data;
    } else {
      // Fallback with simple API client
      const fallbackResponse = await simpleApiClient.get(`/events/?${params.toString()}`);
      finalResponse = fallbackResponse.data;
    }
    
    // Enrich event data regardless of response structure
    if (finalResponse) {
      if ('events' in finalResponse && Array.isArray(finalResponse.events)) {
        // EventListResponse structure
        finalResponse.events = finalResponse.events.map(enrichEventData);
      } else if ('results' in finalResponse && Array.isArray(finalResponse.results)) {
        // Django paginated response
        finalResponse.results = finalResponse.results.map(enrichEventData);
      } else if (Array.isArray(finalResponse)) {
        // Direct array response
        finalResponse = finalResponse.map(enrichEventData);
      }
    }
    
    return finalResponse as EventListResponse;
  },

  // Get event date range for slider initialization
  getEventDateRange: async (): Promise<{min_date: string, max_date: string, today: string}> => {
    const response = await apiClient.get<APIResponse<{min_date: string, max_date: string, today: string}>>(
      `/events/date_range/`,
      { cache: true, ttl: 10 * 60 * 1000 } // Cache for 10 minutes
    );
    
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data;
    }
    
    // Fallback to today if API doesn't return expected structure
    const today = new Date().toISOString().split('T')[0];
    return {
      min_date: today,
      max_date: today,
      today: today
    };
  },

  // Get single event by slug (secure)
  getEventBySlug: async (slug: string): Promise<Event> => {
    
    const response = await apiClient.get<Event>(
      `/events/by-slug/${slug}/`,
      { cache: false } // Disable cache for debugging
    );
    
    // Backend returns event directly for this endpoint, enrich it
    return enrichEventData(response.data);
  },

  // Get single event by ID
  getEvent: async (eventId: string): Promise<Event> => {
    const response = await apiClient.get<APIResponse<Event>>(
      `/events/${eventId}/`,
      { cache: true, ttl: 5 * 60 * 1000 } // Cache for 5 minutes
    );
    
    // Handle different response structures and enrich data
    let eventData: any;
    if (response.data?.data) {
      // Standard APIResponse<Event> structure
      eventData = response.data.data;
    } else if (response.data && typeof response.data === 'object' && 'id' in response.data && 'title' in response.data) {
      // Direct event object response
      eventData = response.data;
    } else {
      throw new Error('Invalid response structure from server');
    }
    
    return enrichEventData(eventData);
  },

  // Create new event with approval workflow
  createEvent: async (eventData: EventFormData, userRole?: string): Promise<Event> => {
    // Convert undefined values AND empty strings to null for backend compatibility
    const backendData = {
      ...eventData,
      // Convert empty strings and undefined to null for optional integer/UUID fields
      category_id: (eventData.category_id === undefined || eventData.category_id === null || (typeof eventData.category_id === 'string' && eventData.category_id === '')) ? null : eventData.category_id,
      series_id: (eventData.series_id === undefined || eventData.series_id === null || (typeof eventData.series_id === 'string' && eventData.series_id === '')) ? null : eventData.series_id,
      host_id: (eventData.host_id === undefined || eventData.host_id === null || (typeof eventData.host_id === 'string' && eventData.host_id === '')) ? null : eventData.host_id,
      guest_limit: (eventData.guest_limit === undefined || eventData.guest_limit === null || (typeof eventData.guest_limit === 'string' && eventData.guest_limit === '')) ? null : eventData.guest_limit,
      editable_until: (eventData.editable_until === undefined || eventData.editable_until === '' || eventData.editable_until === null) ? null : eventData.editable_until,
      // For string fields, keep empty strings as they're allowed
      message_template: eventData.message_template ?? '',
      // link_expiry should be null if empty
      link_expiry: (eventData.link_expiry === undefined || eventData.link_expiry === '' || eventData.link_expiry === null) ? null : eventData.link_expiry
      
      // Note: Backend now handles access_control_flags internally for approval workflow
    };

    const response = await apiClient.post<APIResponse<Event>>('/events/', backendData);
    
    // Invalidate events cache after creating a new event
    await apiClient.invalidateCache('/events/');
    
    return enrichEventData(response.data.data);
  },

  // Update existing event
  // Update event with optimistic updates
  updateEvent: async (eventId: string, eventData: Partial<EventFormData>): Promise<Event> => {
    const response = await apiClient.put<APIResponse<Event>>(`/events/${eventId}/`, eventData);
    
    // Handle different response structures (same logic as getEvent) and enrich
    let responseEventData: any;
    if (response.data?.data) {
      responseEventData = response.data.data;
    } else if (response.data && typeof response.data === 'object' && 'id' in response.data && 'title' in response.data) {
      responseEventData = response.data;
    } else {
      throw new Error('Invalid response structure from server');
    }
    
    return enrichEventData(responseEventData);
  },

  // Enable usher service for an event
  enableUsherService: async (eventId: string): Promise<Event> => {
    const response = await apiClient.post<APIResponse<Event>>(`/events/${eventId}/enable-usher-service/`, {});
    
    // Invalidate events cache after enabling usher service
    await apiClient.invalidateCache('/events/');
    await apiClient.invalidateCache(`/events/${eventId}/`);
    
    return enrichEventData(response.data.data);
  },

  // Delete event
  deleteEvent: async (eventId: string): Promise<void> => {
    await apiClient.delete(`/events/${eventId}/`);
    
    // Invalidate events cache after deletion
    await apiClient.invalidateCache('/events/');
    await apiClient.invalidateCache(`/events/${eventId}/`);
  },

  // Get event categories
  getCategories: async (): Promise<EventCategory[]> => {
    const response = await apiClient.get<EventCategory[]>(
      '/events/categories/',
      { cache: true, ttl: 10 * 60 * 1000 } // Cache for 10 minutes (categories change rarely)
    );
    return response.data;
  },

  // Create event category
  createCategory: async (name: string): Promise<EventCategory> => {
    const response = await apiClient.post<APIResponse<EventCategory>>('/events/categories/', { name });
    return response.data.data;
  },

  // Get event series
  getSeries: async (): Promise<EventSeries[]> => {
    const response = await apiClient.get<EventSeries[]>(
      '/events/series/',
      { cache: true, ttl: 10 * 60 * 1000 } // Cache for 10 minutes (series change rarely)
    );
    return response.data;
  },

  // Create event series
  createSeries: async (name?: string): Promise<EventSeries> => {
    const response = await apiClient.post<APIResponse<EventSeries>>('/events/series/', { name });
    return response.data.data;
  },

  // Get events for current user (host's events)
  getMyEvents: async (
    filters: EventFilters = {},
    page: number = 1,
    pageSize: number = 9
  ): Promise<EventListResponse> => {
    const params = new URLSearchParams();
    
    // Add pagination
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    
    // Add filters
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach(status => params.append('status', status));
    }
    if (filters.category_id) {
      params.append('category', filters.category_id.toString());
    }
    if (filters.date_from) {
      params.append('date_from', filters.date_from);
    }
    if (filters.date_to) {
      params.append('date_to', filters.date_to);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }

    const response = await apiClient.get<APIResponse<EventListResponse>>(`/events/my-events/?${params.toString()}`);
    return response.data.data;
  },

  // Get event statistics
  getEventStats: async (eventId?: string) => {
    const url = eventId ? `/events/${eventId}/stats/` : '/events/stats/';
    const response = await apiClient.get<APIResponse<any>>(url);
    return response.data.data;
  },

  // Get event history
  getEventHistory: async (eventId: string): Promise<EventHistoryEntry[]> => {
    const response = await apiClient.get<APIResponse<EventHistoryEntry[]>>(
      `/events/${eventId}/history/`
    );
    return response.data.data;
  },

  // Export events
  exportEvents: async (
    filters: EventFilters = {},
    format: 'csv' | 'excel' = 'csv'
  ): Promise<Blob> => {
    const params = new URLSearchParams();
    params.append('format', format);
    
    // Add filters
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach(status => params.append('status', status));
    }
    if (filters.category_id) {
      params.append('category', filters.category_id.toString());
    }
    if (filters.date_from) {
      params.append('date_from', filters.date_from);
    }
    if (filters.date_to) {
      params.append('date_to', filters.date_to);
    }

    const response = await apiClient.get(`/events/export/?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Send invitations
  sendInvitations: async (eventId: string, guestIds: string[]): Promise<void> => {
    await apiClient.post(`/events/${eventId}/send-invitations/`, {
      guest_ids: guestIds
    });
  },

  // Resend invitations
  resendInvitations: async (eventId: string, guestIds?: string[]): Promise<void> => {
    await apiClient.post(`/events/${eventId}/resend-invitations/`, {
      guest_ids: guestIds
    });
  },

  // ============================================================================
  // PHASE 2 NEW ENDPOINTS
  // ============================================================================

  // Get event permissions for current user
  getEventPermissions: async (eventId: string): Promise<EventPermissions> => {
    const response = await apiClient.get<APIResponse<{ permissions: EventPermissions }>>(`/events/${eventId}/permissions/`);
    return response.data.data.permissions;
  },

  // Get available status transitions
  getStatusTransitionOptions: async (eventId?: string): Promise<StatusTransitionOptions> => {
    const url = eventId ? `/events/${eventId}/status-options/` : '/events/status-options/';
    const response = await apiClient.get<APIResponse<StatusTransitionOptions>>(url);
    return response.data.data;
  },

  // Update event status with validation
  updateEventStatus: async (eventId: string, newStatus: string, actionData: EventActionData): Promise<Event> => {
    const response = await apiClient.post<APIResponse<Event>>(`/events/${eventId}/update-status/`, {
      status: newStatus,
      ...actionData
    });
    return response.data.data;
  },

  // Cancel event
  cancelEvent: async (eventId: string, actionData: EventActionData): Promise<Event> => {
    const response = await apiClient.post<APIResponse<Event>>(`/events/${eventId}/cancel/`, actionData);
    return response.data.data;
  },

  // Reschedule event
  rescheduleEvent: async (eventId: string, actionData: EventActionData): Promise<Event> => {
    const response = await apiClient.post<APIResponse<Event>>(`/events/${eventId}/reschedule/`, actionData);
    return response.data.data;
  },

  // Archive event
  archiveEvent: async (eventId: string, actionData: EventActionData): Promise<Event> => {
    const response = await apiClient.post<APIResponse<Event>>(`/events/${eventId}/archive/`, actionData);
    return response.data.data;
  },

  // Reactivate event
  reactivateEvent: async (eventId: string, actionData: EventActionData): Promise<Event> => {
    const response = await apiClient.post<APIResponse<Event>>(`/events/${eventId}/reactivate/`, actionData);
    return response.data.data;
  },

  // Approve pending event (Admin only) - Using dedicated approve endpoint
  approveEvent: async (eventId: string, actionData: EventActionData = {}): Promise<Event> => {
    const response = await apiClient.post<APIResponse<Event>>(`/events/${eventId}/approve/`, {
      reason: actionData.reason || 'Event approved by admin'
    });
    
    // Invalidate events cache after approval
    apiClient.invalidateCache('/events/');
    // Also invalidate dashboard cache since pending approvals will change
    apiClient.invalidateCache('/dashboard/');
    
    return response.data.data || response.data;
  },

  // Reject pending event (Admin only) - Using dedicated reject endpoint
  rejectEvent: async (eventId: string, actionData: EventActionData): Promise<Event> => {
    const response = await apiClient.post<APIResponse<Event>>(`/events/${eventId}/reject/`, {
      reason: actionData.reason || 'Event rejected by admin'
    });
    
    // Invalidate events cache after rejection
    apiClient.invalidateCache('/events/');
    // Also invalidate dashboard cache since pending approvals will change
    apiClient.invalidateCache('/dashboard/');
    
    return response.data.data || response.data;
  },

  // Duplicate event
  duplicateEvent: async (eventId: string, newTitle?: string, newDate?: string): Promise<Event> => {
    const response = await apiClient.post<APIResponse<Event>>(`/events/${eventId}/duplicate/`, {
      title: newTitle,
      event_date: newDate
    });
    return response.data.data;
  },

  // Get invitation link data
  getInvitationLinkData: async (eventId: string): Promise<InvitationLinkData> => {
    const response = await apiClient.get<APIResponse<Event>>(`/events/${eventId}/`);
    const event = response.data.data;
    
    return {
      uuid: event.invitation_link_uuid,
      invitation_url: event.invitation_url,
      share_text: `You're invited to ${event.title} on ${event.event_date}`,
      email_subject: `Invitation: ${event.title}`,
      event_title: event.title,
      expires_at: event.link_expiry
    };
  },

  // Refresh invitation link
  refreshInvitationLink: async (eventId: string, extendDays: number = 30): Promise<InvitationLinkData> => {
    const response = await apiClient.post<APIResponse<Event>>(`/events/${eventId}/refresh-link/`, {
      extend_days: extendDays
    });
    const event = response.data.data;
    
    return {
      uuid: event.invitation_link_uuid,
      invitation_url: event.invitation_url,
      share_text: `You're invited to ${event.title} on ${event.event_date}`,
      email_subject: `Invitation: ${event.title}`,
      event_title: event.title,
      expires_at: event.link_expiry
    };
  },

  // Validate event data (business rules)
  validateEventData: async (eventData: EventFormData): Promise<BusinessRuleValidation> => {
    const response = await apiClient.post<APIResponse<BusinessRuleValidation>>('/events/validate/', {
      ...eventData,
      validate_only: true
    });
    return response.data.data;
  },

  // Bulk status update
  bulkStatusUpdate: async (eventIds: string[], newStatus: string, actionData: EventActionData): Promise<Event[]> => {
    const response = await apiClient.post<APIResponse<Event[]>>('/events/bulk-status-update/', {
      event_ids: eventIds,
      status: newStatus,
      ...actionData
    });
    return response.data.data;
  },

  // Get event lifecycle summary
  getEventLifecycleSummary: async (eventId: string): Promise<any> => {
    const response = await apiClient.get<APIResponse<any>>(`/events/${eventId}/lifecycle/`);
    return response.data.data;
  },

  // Get unique locations for dropdown
  getLocationOptions: async (): Promise<string[]> => {
    const response = await apiClient.get<{ success: boolean; locations: string[]; total_count: number }>('/events/location-options/');
    return response.data.locations;
  },

  // Get all events in a specific series
  getSeriesEvents: async (seriesId: string): Promise<{ series: EventSeries; events: Event[] }> => {
    const response = await apiClient.get<APIResponse<{ series: EventSeries; events: Event[] }>>(
      `/events/series/${seriesId}/events/`,
      { cache: true, ttl: 2 * 60 * 1000 } // Cache for 2 minutes
    );
    
    // Enrich event data
    const enrichedEvents = response.data.data.events.map(enrichEventData);
    
    return {
      series: response.data.data.series,
      events: enrichedEvents
    };
  },

  // Send reschedule notifications to selected guests
  rescheduleNotifyGuests: async (eventId: string, guestIds: string[]): Promise<{
    success: boolean;
    message: string;
    notification_results: {
      sent_count: number;
      failed_count: number;
      total_count: number;
    };
    error?: {
      message: string;
    };
  }> => {
    
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      notification_results: {
        sent_count: number;
        failed_count: number;
        total_count: number;
      };
      error?: {
        message: string;
      };
    }>(`/events/${eventId}/reschedule-notify/`, {
      guest_ids: guestIds
    });
    
    return response.data;
  }
};

export default eventAPI;
